import React from 'react';

import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SxProps,
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';

import { HomeBottomButtonOptionsSettingsProps } from './interfaces';
import { useStyles } from './styles';

/**
 * Renders a labeled MUI `Select` used across the settings/options screens
 * (navigation mode, camera, mic, speakers, language, quality, etc).
 *
 * @param props.devices - Options to list, each rendered as a `MenuItem`.
 * @param props.handleSelect - Called with the native change event when a device is chosen.
 * @param props.selectedValue - Currently selected device id; falls back to the first device if no match.
 * @param props.label - Optional label rendered above the select.
 * @param props.disabled - Disables the select.
 * @param props.sx - Additional `sx` overrides merged onto the select.
 * @param props.testId - `data-testid` applied to the select and, suffixed per option, to each `MenuItem`.
 * @param props.IconComponent - Overrides the dropdown arrow; defaults to `KeyboardArrowDown`.
 */
const HomeBottomButtonOptionsSettings = ({
  devices,
  handleSelect,
  selectedValue,
  label = '',
  disabled = false,
  sx = {},
  testId,
  IconComponent = KeyboardArrowDown,
}: HomeBottomButtonOptionsSettingsProps) => {
  const classes = useStyles();

  let activeDevice = selectedValue;
  if (devices.length) {
    activeDevice = devices.find(({ deviceId }) => deviceId === selectedValue)
      ? selectedValue
      : devices[0].deviceId;
  }

  return (
    <FormControl sx={classes.root}>
      {label ? <InputLabel sx={classes.label}>{label}</InputLabel> : null}
      <Select
        label={label}
        value={activeDevice}
        onChange={handleSelect}
        disableUnderline
        variant="standard"
        sx={{ ...classes.select, ...sx } as SxProps}
        IconComponent={IconComponent}
        disabled={disabled}
        data-testid={testId}
        MenuProps={{
          sx: { zIndex: 10003 },
          PaperProps: { sx: classes.menuPaper },
          MenuListProps: { sx: classes.menuList },
        }}
      >
        {devices.map((device: any) => (
          <MenuItem
            value={device.deviceId}
            key={device.deviceId}
            sx={classes.menuItem}
            data-testid={testId ? `${testId}-option-${device.deviceId}` : undefined}
          >
            {device.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default HomeBottomButtonOptionsSettings;
